import { gqlTagGira } from '@atlassian/jira-apollo-gira';
import { CONFLUENCE_LINKS_FRAGMENT } from '@atlassian/jira-business-confluence-pages/src/common/graphql/gql.tsx';
import {
	JIRA_BUSINESS_FIELDS_FRAGMENT,
	JIRA_BUSINESS_TOP_LEVEL_FIELD_FRAGMENT,
} from '@atlassian/jira-business-fields/src/common/fragment/gql.tsx';

export const LIST_VIEW_FIELDS_FRAGMENT = gqlTagGira`
  fragment ListViewIssueFieldsFragment on JiraWorkManagementViewItem {
    fields: fieldsByIdOrAlias(idsOrAliases: $fieldIds, ignoreMissingFields: true) @connection (key: "fields") {
      fieldId
      name
      aliasFieldId
      type
      ... on JiraIssueTypeField {
        issueType {
          issueTypeId
          name
          avatar {
            medium
          }
          hierarchy {
            level
          }
        }
      }
      ... on JiraStatusField {
        status {
          name
          statusId
          statusCategory {
            statusCategoryId
          }
        }
      }
      ... on JiraRichTextField {
        richText {
          adfValue {
            json
            convertedPlainText {
              plainText
              isTruncated
            }
          }
        }
        renderer
      }
      ... on JiraOriginalTimeEstimateField {
        originalEstimate {
          timeInSeconds
        }
        fieldConfig {
          isEditable
        }
      }
      ... on JiraTimeTrackingField {
        originalEstimate {
          timeInSeconds
        }
        timeTrackingSettings {
          defaultUnit
          defaultFormat
          workingHoursPerDay
          workingDaysPerWeek
        }
      }
      ... on JiraComponentsField {
        fieldConfig {
          isEditable
        }
        selectedComponentsConnection {
          edges {
            node {
              name
              componentId
            }
          }
        }
      }
      ... on JiraParentIssueField {
        parentIssue {
          issueId
          key
        }
      }
     ... on JiraCheckboxesField {
        selectedOptions {
          edges {
            node {
              optionId
              value
            }
          }
        }
      }
      ... on JiraSprintField {
        fieldId
        aliasFieldId
        fieldConfig {
          isEditable
        }
        selectedSprintsConnection(first: 1) {
          edges {
            node {
              sprintId
              name
              state
            }
          }
        }
      }
      ...jiraBusinessSingleVersionFieldFragment
      ...jiraBusinessMultipleVersionFieldFragment
      ...jiraBusinessParentFieldFragment
      ...jiraBusinessTeamFieldFragment
      ...JiraGoalsFieldFragment
      ...jiraBusinessResolutionFieldFragment
      ...jiraBusinessSingleGroupFieldFragment
      ...jiraBusinessMultipleGroupFieldFragment
      ...jiraLabelsFieldFragment
      ...jiraDateFieldFragment
      ...jiraDateTimeFieldFragment
      ...jiraSingleUserFieldFragment
      ...jiraMultipleUserFieldFragment
      ...jiraPeopleFieldFragment
      ...jiraPriorityFieldFragment
      ...jiraMultipleSelectFieldFragment
      ...jiraNumberFieldFragment
      ...jiraRadioSelectFieldFragment
      ...jiraSingleSelectFragment
      ...jiraUrlFieldFragment
      ...jiraSingleLineTextFragment
      ...jiraStatusCategoryFragment
      ...jiraCascadingSelectFieldFragment
      ...jiraProjectFieldFragment
    }
  }

  ${JIRA_BUSINESS_FIELDS_FRAGMENT}
`;

export const LIST_VIEW_EDGES_FRAGMENT = gqlTagGira`
  fragment ListViewItemEdges on JiraWorkManagementViewItemConnection {
    edges {
      node {
        id
        issueId
        childSummary(includeDone: $includeDoneItems) {
            hasChildren
            totalCount
        }
        commentSummary {
            totalCount
        }
        legacyConfluenceLinks(getFirstOnly: true) @include(if: $withConfluenceLinks) @connection(key: "legacyConfluenceLinks") {
            ...ConfluenceLinksFragment
        }
        devSummaryCache @include(if: $isDevSummaryFieldVisible)  {
          ...DevSummaryFieldFragment
        }
        ...ListViewIssueFieldsFragment
      }
    }
  }
  ${LIST_VIEW_FIELDS_FRAGMENT}
  ${CONFLUENCE_LINKS_FRAGMENT}
  ${JIRA_BUSINESS_TOP_LEVEL_FIELD_FRAGMENT}
`;

export const LIST_VIEW_QUERY = gqlTagGira`
  query JWMListViewItems($cloudId: ID!, $jql: String!, $includeDoneItems: Boolean!, $fieldIds: [String]!, $first: Int, $after: String, $withConfluenceLinks: Boolean!, $isParentFieldVisible: Boolean!, $isDevSummaryFieldVisible: Boolean!, $withParentIssueId: Boolean!) {
    jira {
      list: jwmViewItems(
        cloudId: $cloudId,
        jql: $jql,
        first: $first,
        after: $after,
      ) @connection (key: "list", filter:["jql"]) {
        ...on QueryError {
          message
          extensions {
            statusCode
            errorType
          }
        }
        ... on JiraWorkManagementViewItemConnection {
          ...ListViewItemEdges
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }

  ${LIST_VIEW_EDGES_FRAGMENT}
`;

export const BACKGROUND_LIST_VIEW_QUERY = gqlTagGira`
  query JWMListViewItemsBackground($cloudId: ID!, $jql: String!, $includeDoneItems: Boolean!, $fieldIds: [String]!, $first: Int, $after: String, $withConfluenceLinks: Boolean!, $isParentFieldVisible: Boolean!, $isDevSummaryFieldVisible: Boolean!, $withParentIssueId: Boolean!) {
    jira {
      list: jwmViewItems(
        cloudId: $cloudId,
        jql: $jql,
        first: $first,
        after: $after,
      ) @connection (key: "backgroundList", filter:["jql"]) {
        ... on JiraWorkManagementViewItemConnection {
          ...ListViewItemEdges
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }

  ${LIST_VIEW_EDGES_FRAGMENT}
`;

// this query will be used to load updated/created items on demand. These items will be added to the main list
export const LIST_VIEW_ITEM_DETAILS_QUERY = gqlTagGira`
  query JWMListViewItemDetails($cloudId: ID!, $jql: String!, $includeDoneItems: Boolean!, $fieldIds: [String]!, $first: Int, $after: String, $withConfluenceLinks: Boolean!, $isParentFieldVisible: Boolean!, $isDevSummaryFieldVisible: Boolean!, $withParentIssueId: Boolean!) {
    jira {
      list: jwmViewItems(
        cloudId: $cloudId,
        jql: $jql,
        first: $first,
        after: $after,
      ) {
        ... on JiraWorkManagementViewItemConnection {
          ...ListViewItemEdges
        }
      }
    }
  }

  ${LIST_VIEW_EDGES_FRAGMENT}
`;
